import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Wifi, Globe, Star, ExternalLink, ChevronDown, AlertCircle, Clock } from 'lucide-react';
import { ESIMDeviceCompatibilityModal } from './ESIMDeviceCompatibilityModal';

interface ESIMProvider {
  id: string;
  name: string;
  logo: string;
  rating: number;
  dataPlans: {
    data: string;
    duration: string;
    price: string;
    currency: string;
  }[];
  coverage: string[];
  features: string[];
  website: string;
  lastUpdated: string;
  note?: string;
}

interface ESIMRecommendationsProps {
  destination?: string;
  destinationCountry?: string;
}

export function ESIMRecommendations({ destination, destinationCountry }: ESIMRecommendationsProps) {
  const [providers, setProviders] = useState<ESIMProvider[]>([]);
  const [loading, setLoading] = useState(false);

  // eSIM providers data — verified July 2026.
  // Note: Prices are typical starting prices in USD and vary by destination. Always verify on the provider's website.
  const mockProviders: ESIMProvider[] = [
    {
      id: '1',
      name: 'Airalo',
      logo: '📡',
      rating: 4.7,
      dataPlans: [
        { data: '1GB', duration: '7 days', price: '4.50', currency: 'USD' },
        { data: '3GB', duration: '30 days', price: '11.50', currency: 'USD' },
        { data: '5GB', duration: '30 days', price: '16.50', currency: 'USD' },
        { data: '10GB', duration: '30 days', price: '27.00', currency: 'USD' },
        { data: '20GB', duration: '30 days', price: '43.00', currency: 'USD' },
        { data: '10GB (global)', duration: '30 days', price: '39.00', currency: 'USD' }
      ],
      coverage: ['Global (200+ countries)', 'Regional bundles', 'Europe', 'Asia-Pacific', 'Americas', 'Africa', 'Middle East'],
      features: ['Instant QR activation', '24/7 multilingual support', 'Top-up anytime', 'Unlimited plans in select countries', 'Multi-device support', 'Airmoney loyalty rewards'],
      website: 'https://www.airalo.com',
      lastUpdated: 'July 2026',
      note: 'The safest all-rounder: widest coverage and a polished app, with mid-pack pricing. Local plans are far cheaper than global ones — Europe and Asia are typically the best value.'
    },
    {
      id: '2',
      name: 'Nomad eSIM',
      logo: '🛰️',
      rating: 4.7,
      dataPlans: [
        { data: '1GB', duration: '7 days', price: '5.00', currency: 'USD' },
        { data: '3GB', duration: '30 days', price: '13.00', currency: 'USD' },
        { data: '5GB', duration: '30 days', price: '18.00', currency: 'USD' },
        { data: '10GB', duration: '30 days', price: '28.00', currency: 'USD' },
        { data: '20GB', duration: '30 days', price: '47.00', currency: 'USD' },
        { data: 'Regional (Europe/Asia)', duration: 'from 7 days', price: '9.00', currency: 'USD' }
      ],
      coverage: ['Global (170+ countries)', 'Regional bundles', 'Europe', 'Americas', 'Asia-Pacific'],
      features: ['Best value per GB in Europe & Asia', 'Data rollover on select plans', 'Multi-country regional plans', 'Instant activation', 'Top-up support', 'Strong customer service'],
      website: 'https://www.getnomad.app',
      lastUpdated: 'July 2026',
      note: 'Often the cheapest per-GB option for Europe and Asia, and strong in North America. Great for multi-country trips thanks to regional bundles.'
    },
    {
      id: '3',
      name: 'Saily (by Nord Security)',
      logo: '🔐',
      rating: 4.6,
      dataPlans: [
        { data: '1GB', duration: '7 days', price: '3.99', currency: 'USD' },
        { data: '3GB', duration: '30 days', price: '8.99', currency: 'USD' },
        { data: '5GB', duration: '30 days', price: '12.99', currency: 'USD' },
        { data: '10GB', duration: '30 days', price: '22.99', currency: 'USD' },
        { data: '10GB (global)', duration: '30 days', price: '33.00', currency: 'USD' },
        { data: 'Unlimited (Saily Ultra)', duration: '30 days', price: '59.99', currency: 'USD' }
      ],
      coverage: ['Global (200+ countries)', 'Europe', 'Americas', 'Asia', 'Middle East', 'Africa'],
      features: ['Aggressive pricing, frequent promos', 'Built by the NordVPN team', 'Built-in ad & malware blocking', 'Simple setup app', 'Multi-device support', 'Saily Ultra bundles unlimited data + Nord Security suite'],
      website: 'https://saily.com',
      lastUpdated: 'July 2026',
      note: 'The budget pick. Cheapest small-data plans of the four, though speeds can be throttled in some countries during peak times.'
    },
    {
      id: '4',
      name: 'Holafly',
      logo: '🌐',
      rating: 4.5,
      dataPlans: [
        { data: 'Unlimited', duration: '5 days', price: '19.00', currency: 'USD' },
        { data: 'Unlimited', duration: '7 days', price: '29.00', currency: 'USD' },
        { data: 'Unlimited', duration: '10 days', price: '37.00', currency: 'USD' },
        { data: 'Unlimited (Europe, 35+ countries)', duration: '15 days', price: '59.00', currency: 'USD' },
        { data: 'Unlimited', duration: '30 days', price: '79.00', currency: 'USD' },
        { data: 'Unlimited (monthly subscription)', duration: 'recurring', price: '64.90', currency: 'USD' }
      ],
      coverage: ['Europe (40+ countries)', 'USA & Canada', 'Latin America', 'Asia', 'Oceania', 'Middle East', 'Africa', 'Global plan (160+ countries)'],
      features: ['Truly unlimited data', 'Hotspot/tethering on most plans', 'Calls & texts on select plans', 'Easy QR installation', '24/7 support', 'Keep your WhatsApp number'],
      website: 'https://esim.holafly.com',
      lastUpdated: 'July 2026',
      note: 'Best for heavy users who do not want to count GB — priced per day rather than per GB, so it only wins on longer or data-hungry trips. Fair use policy applies (speeds drop after high daily usage).'
    }
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      setProviders(mockProviders);
      setLoading(false);
    }, 1000);
  }, [destination, destinationCountry]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wifi className="h-5 w-5" />
            eSIM Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wifi className="h-5 w-5" />
          eSIM Recommendations
        </CardTitle>
        <CardDescription>
          {destinationCountry ? `Best eSIM providers for ${destinationCountry}` : 'Popular eSIM providers for international travel'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6">
        {/* Pricing Disclaimer */}
        <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Important:</strong> Prices shown are approximate and may vary by destination, season, and promotions. 
            Always check the provider's website for current pricing and availability for your specific destination.
          </AlertDescription>
        </Alert>
        {providers.map((provider) => (
          <Collapsible key={provider.id} className="w-full">
            <Card className="border-l-4 border-l-primary/20 overflow-hidden">
              {/* Collapsible Trigger - Provider Header */}
              <CollapsibleTrigger className="w-full">
                <CardHeader className="pb-3 sm:pb-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl sm:text-3xl">{provider.logo}</span>
                      <div className="text-left">
                        <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                          {provider.name}
                          <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                        </CardTitle>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <span>{provider.rating} rating</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <Clock className="h-3 w-3" />
                            <span>Updated {provider.lastUpdated}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        asChild
                        onClick={(e) => e.stopPropagation()}
                        className="hidden sm:flex"
                      >
                        <a href={provider.website} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Visit Site
                        </a>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        asChild
                        onClick={(e) => e.stopPropagation()}
                        className="sm:hidden"
                      >
                        <a href={provider.website} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>

              {/* Collapsible Content - Provider Details */}
              <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                <CardContent className="pt-0 pb-4 sm:pb-6 space-y-4 sm:space-y-6">
                  {/* Provider Note */}
                  {provider.note && (
                    <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-900">
                      <AlertCircle className="h-4 w-4 text-cyan-600" />
                      <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
                        {provider.note}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Data Plans */}
                  <div>
                    <h4 className="font-semibold mb-3 text-base sm:text-lg flex items-center gap-2">
                      💳 Data Plans (Starting From)
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {provider.dataPlans.map((plan, index) => (
                        <div key={index} className="bg-gradient-to-br from-secondary/50 to-secondary/30 rounded-xl p-4 text-center border border-border/50 hover:shadow-md transition-shadow">
                          <div className="font-bold text-lg text-primary">{plan.data}</div>
                          <div className="text-sm text-muted-foreground mb-1">{plan.duration}</div>
                          <div className="text-base font-semibold">From ${plan.price}</div>
                          <div className="text-xs text-muted-foreground mt-1">Check website for exact pricing</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div>
                    <h4 className="font-semibold mb-3 text-base sm:text-lg flex items-center gap-2">
                      🌍 Coverage Areas
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {provider.coverage.map((region, index) => (
                        <Badge key={index} variant="secondary" className="px-3 py-1 text-sm">
                          <Globe className="h-3 w-3 mr-1" />
                          {region}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    <h4 className="font-semibold mb-3 text-base sm:text-lg flex items-center gap-2">
                      ⭐ Key Features
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {provider.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                          <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mobile Visit Site Button */}
                  <div className="pt-2 sm:hidden">
                    <Button 
                      className="w-full" 
                      variant="default" 
                      asChild
                    >
                      <a href={provider.website} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Visit {provider.name} Website
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}

        {/* Pro Tips Section */}
        <div className="bg-gradient-to-r from-blue-50 to-teal-500 border border-blue-200 rounded-xl p-4 sm:p-6">
          <h4 className="font-semibold text-blue-900 mb-3 text-base sm:text-lg flex items-center gap-2">
            💡 Pro Tips for eSIM Success
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div>
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-sm text-blue-800">Verify your phone supports eSIM before purchasing</span>
                <ESIMDeviceCompatibilityModal />
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div>
              <span className="text-sm text-blue-800">Download and install the eSIM before your trip</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div>
              <span className="text-sm text-blue-800">Keep your original SIM card as a backup</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div>
              <span className="text-sm text-blue-800">Compare data vs. unlimited plans based on usage</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}